// src/components/ui/EnvironmentBadge.tsx
// Shows a visual indicator for the current environment (Dev, Beta, or hidden in Production)

import React from 'react';
import { Code, FlaskConical, CheckCircle } from 'lucide-react';
import { config } from '../../config/environment';

type Environment = 'development' | 'beta' | 'production';

interface EnvironmentBadgeProps {
  /** Override the environment detection */
  environment?: Environment;
  /** Position of the badge */
  position?: 'banner' | 'corner' | 'inline';
  /** Show additional details like API URL */
  showDetails?: boolean;
}

const getEnvironmentFromConfig = (): Environment => {
  const apiUrl = config.api.baseUrl?.toLowerCase() || '';
  const envSetting = config.environment?.toLowerCase() || '';
  
  // Check environment setting first
  if (envSetting === 'production') return 'production';
  if (envSetting === 'staging' || envSetting === 'beta') return 'beta';
  
  // Fallback to API URL detection
  if (apiUrl.includes('prod')) return 'production';
  if (apiUrl.includes('beta')) return 'beta';
  
  return 'development';
};

const environmentConfig = {
  development: {
    label: 'DEV',
    fullLabel: 'Development Environment',
    bgColor: 'bg-amber-500',
    textColor: 'text-amber-950',
    borderColor: 'border-amber-600',
    Icon: Code,
  },
  beta: {
    label: 'BETA',
    fullLabel: 'Beta Testing Environment',
    bgColor: 'bg-violet-500',
    textColor: 'text-white',
    borderColor: 'border-violet-600',
    Icon: FlaskConical,
  },
  production: {
    label: 'PROD',
    fullLabel: 'Production',
    bgColor: 'bg-emerald-500',
    textColor: 'text-white',
    borderColor: 'border-emerald-600',
    Icon: CheckCircle,
  },
};

export const EnvironmentBadge: React.FC<EnvironmentBadgeProps> = ({
  environment,
  position = 'banner',
  showDetails = false,
}) => {
  const env = environment || getEnvironmentFromConfig();
  
  // Don't show badge in production
  if (env === 'production') {
    return null;
  }
  
  const envConfig = environmentConfig[env];
  const { Icon } = envConfig;
  
  if (position === 'corner') {
    // Fixed position corner badge
    return (
      <div 
        className={`fixed top-0 right-0 z-50 ${envConfig.bgColor} ${envConfig.textColor} 
                    px-3 py-1 text-xs font-bold uppercase tracking-wider
                    rounded-bl-lg shadow-lg border-l border-b ${envConfig.borderColor}`}
        title={envConfig.fullLabel}
      >
        <Icon className="inline-block w-3 h-3 mr-1" />
        {envConfig.label}
      </div>
    );
  }
  
  if (position === 'inline') {
    // Inline badge for use in headers
    return (
      <span 
        className={`${envConfig.bgColor} ${envConfig.textColor} 
                    px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider
                    rounded-full shadow-sm inline-flex items-center gap-1`}
        title={envConfig.fullLabel}
      >
        <Icon className="w-3 h-3" />
        {envConfig.label}
      </span>
    );
  }
  
  // Banner style (default)
  return (
    <div 
      className={`${envConfig.bgColor} ${envConfig.textColor} 
                  px-4 py-1.5 text-xs font-semibold text-center 
                  flex items-center justify-center gap-2 relative z-30`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{envConfig.fullLabel}</span>
      {showDetails && config.api.baseUrl && (
        <span className="opacity-75 ml-2">
          → {new URL(config.api.baseUrl).hostname}
        </span>
      )}
    </div>
  );
};

export default EnvironmentBadge;
